import { Avatar, Button, Empty, Form, Input, InputNumber, Modal, Select, Spin, Typography, Upload, message, theme } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_BASE_URL } from "../config/serverApiConfig";
import { getBase64 } from "../utils/history";
import "./matter/LabPreview.css";
import { DEFAULT_AVATAR, ROLES, ROLE_LABELS, ROLE_COLORS, dummyRequest, initials } from "./hrRoles";

const { Title } = Typography;

function Hr() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [hrForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [hr, setHr] = useState([]);
  const [selectedId, setSelectedId] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(DEFAULT_AVATAR);

  const handleUploadChange = (e) => {
    if (!e.file?.originFileObj) return;
    getBase64(e.file.originFileObj, (url64) => setImageUrl(url64));
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("تەنها فایلی JPG/PNG قبووڵە");
    }
    return isJpgOrPng;
  };

  const loadStaff = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}user`)
      .then((res) => setHr(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setHr([]);
        message.error("نەتوانرا کارمەندەکان بهێنرێن");
      })
      .finally(() => setLoading(false));
  };

  const closeAdd = () => {
    setOpen(false);
    hrForm.resetFields();
    setImageUrl(DEFAULT_AVATAR);
  };

  const submitHr = (values) => {
    setConfirmLoading(true);
    const { image, ...lawyer } = values;
    const fd = new FormData();

    if (image?.fileList?.length) {
      fd.append("image", image.fileList[0].originFileObj);
    }

    fd.append("user", JSON.stringify(lawyer));

    axios
      .post(`${API_BASE_URL}user`, fd)
      .then((res) => {
        setHr((prev) => [res.data, ...prev]);
        closeAdd();
        message.success("کارمەند زیادکرا");
      })
      .catch(() => {
        message.error("نەتوانرا کارمەند زیاد بکرێت");
      })
      .finally(() => setConfirmLoading(false));
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hr.filter((item) => {
      const roleOk = selectedId === "all" || item.role === selectedId;
      if (!roleOk) return false;
      if (!q) return true;
      return [item.name, item.username, item.phoneNumber].some((value) => (value || "").toLowerCase().includes(q));
    });
  }, [hr, query, selectedId]);

  return (
    <div
      className="lab-page"
      style={{
        "--lab-surface": token.colorBgContainer,
        "--note-accent": token.colorPrimary,
        "--note-accent-soft": token.colorPrimaryBg,
      }}
    >
      <div className="lab-page-head">
        <div>
          <Title level={3} style={{ color: token.colorPrimary }}>
            کارمەند
          </Title>
          <p>بەڕێوەبردنی کارمەندانی کلینیک</p>
        </div>
        <span className="lab-count">{filtered.length} کارمەند</span>
      </div>

      <div className="lab-form-card note-toolbar">
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined />}
          placeholder="گەڕان بە ناو، بەکارهێنەر یان مۆبایل"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="hr-toolbar-actions">
          <Button size="large" icon={<SyncOutlined spin={loading} />} onClick={loadStaff}>
            نوێکردنەوە
          </Button>
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            زیادکردن
          </Button>
        </div>
      </div>

      <div className="hr-filters">
        {ROLES.map((role) => (
          <button
            key={role.id}
            type="button"
            className={`hr-chip${selectedId === role.id ? " is-on" : ""}`}
            onClick={() => setSelectedId(role.id)}
          >
            {role.label}
          </button>
        ))}
      </div>

      {loading && hr.length === 0 ? (
        <div className="cliche-empty">
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <div className="cliche-empty">
          <Empty description="هیچ کارمەندێک نییە" />
        </div>
      ) : (
        <div className="hr-grid">
          {filtered.map((item) => (
            <button key={item.id} type="button" className="hr-card" onClick={() => navigate(`/HR/${item.id}`)}>
              <div className="hr-card-top">
                <div className="hr-person">
                  <Avatar size={56} src={item.image || undefined} style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}>
                    {initials(item.name)}
                  </Avatar>
                  <div className="hr-copy">
                    <h3 className="hr-name">{item.name || "—"}</h3>
                    <p className="hr-meta">{item.username || "بێ بەکارهێنەر"}</p>
                  </div>
                </div>
                <span className="hr-role" style={{ backgroundColor: ROLE_COLORS[item.role] || token.colorPrimary }}>
                  {ROLE_LABELS[item.role] || item.role}
                </span>
              </div>
              <div className="hr-card-foot">
                <span className="cliche-tag">{item.phoneNumber || "بێ مۆبایل"}</span>
                <span className={`hr-status${item.status === false ? " is-off" : ""}`}>{item.status === false ? "ناچالاک" : "چالاک"}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={open}
        centered
        okText="زیادکردن"
        cancelText="پاشگەزبوونەوە"
        confirmLoading={confirmLoading}
        onOk={() => hrForm.submit()}
        onCancel={closeAdd}
        title={
          <Title style={{ color: token.colorPrimary }} level={5}>
            زیادکردنی کارمەند
          </Title>
        }
      >
        <Form name="AWS" form={hrForm} layout="vertical" onFinish={submitHr}>
          <Form.Item name="image" className="hr-avatar-field">
            <Upload listType="picture" customRequest={dummyRequest} showUploadList={false} beforeUpload={beforeUpload} onChange={handleUploadChange}>
              <img src={imageUrl} alt="کارمەند" className="hr-avatar-upload" />
            </Upload>
          </Form.Item>

          <Form.Item label="ناوی کارمەند" name="name" rules={[{ required: true, message: "ناو پێویستە" }]}>
            <Input size="large" placeholder="ناوی کارمەند" />
          </Form.Item>

          <Form.Item label="ناوی بەکارهێنەر" name="username" rules={[{ required: true, message: "ناوی بەکارهێنەر پێویستە" }]}>
            <Input size="large" placeholder="ناوی بەکارهێنەر" />
          </Form.Item>

          <Form.Item label="ژم. مۆبایل" name="phoneNumber">
            <Input size="large" placeholder="ژم. مۆبایل" />
          </Form.Item>

          <Form.Item label="ڕەگەز" name="gender">
            <Select size="large" placeholder="ڕەگەز">
              <Select.Option value="نێر">نێر</Select.Option>
              <Select.Option value="مێ">مێ</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="ڕۆڵ" name="role" rules={[{ required: true, message: "ڕۆڵ پێویستە" }]}>
            <Select size="large" placeholder="ڕۆڵ">
              {ROLES.filter((role) => role.id !== "all").map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="تەمەن" name="age">
            <InputNumber size="large" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="وشەی نهێنی" name="password" rules={[{ required: true, message: "وشەی نهێنی پێویستە" }]}>
            <Input.Password size="large" placeholder="وشەی نهێنی" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Hr;
